import { format } from 'date-fns';

import {
  DateInput,
  DatePicker,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Label,
  Textarea,
} from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { FIRE_SAFETY } from '../../../constants';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function FireSafety({ section, setSectionValues }: Props) {
  function setFireExtinguisherLocationDescription(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_LOCATION]: event.currentTarget.value,
    });
  }

  function onFireExtinguisherServiceDateChange(formattedDate: string): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_SERVICE_DATE]: formattedDate,
    });
  }

  function setEmergencyExitDescription(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.EMERGENCY_EXIT_LOCATION]: event.currentTarget.value,
    });
  }

  function onSmokeAlarmReplacedDateChange(formattedDate: string): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.SMOKE_ALARM_REPLACED_DATE]: formattedDate,
    });
  }

  function onCarbonMonoxideAlarmReplacedDateChange(formattedDate: string): void {
    setSectionValues({
      [FIRE_SAFETY.FIELD_NAME.CARBON_MONOXIDE_ALARM_REPLACED_DATE]: formattedDate,
    });
  }

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs">
        What are the fire safety features details for the property?
      </Heading>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Describe the location of the fire extinguishers on the tenant’s floor</Label>}
        control={
          <Textarea
            css={{ resize: 'vertical' }}
            onChange={setFireExtinguisherLocationDescription}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_LOCATION)}
          />
        }
      />
      <FormField
        required
        label={<Label>Fire extinguisher service date</Label>}
        control={
          <DatePicker
            dateFormat={DATE_PICKER_FORMAT}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.FIRE_EXTINGUISHER_SERVICE_DATE)}
            onValueChange={(value) => {
              const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
              onFireExtinguisherServiceDateChange(formatted);
            }}
            maxDate={new Date()}
            closeModalOnDateSelect
            input={<DateInput />}
          />
        }
      />
      <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
      <FormField
        required
        label={<Label>Describe the location of the emergency exits on the tenant’s floor</Label>}
        control={
          <Textarea
            css={{ resize: 'vertical' }}
            onChange={setEmergencyExitDescription}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.EMERGENCY_EXIT_LOCATION)}
          />
        }
        description={<FormHelp>Emergency exits shall remain unobstructed at all times</FormHelp>}
      />
      <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Smoke alarms last replaced date</Label>}
        control={
          <DatePicker
            dateFormat={DATE_PICKER_FORMAT}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.SMOKE_ALARM_REPLACED_DATE)}
            onValueChange={(value) => {
              const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
              onSmokeAlarmReplacedDateChange(formatted);
            }}
            maxDate={new Date()}
            closeModalOnDateSelect
            input={<DateInput />}
          />
        }
      />
      <FormField
        required
        label={<Label>Carbon monoxide alarms last replaced date</Label>}
        control={
          <DatePicker
            dateFormat={DATE_PICKER_FORMAT}
            value={section.getFieldValue(FIRE_SAFETY.FIELD_NAME.CARBON_MONOXIDE_ALARM_REPLACED_DATE)}
            onValueChange={(value) => {
              const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
              onCarbonMonoxideAlarmReplacedDateChange(formatted);
            }}
            maxDate={new Date()}
            closeModalOnDateSelect
            input={<DateInput />}
          />
        }
      />
    </>
  );
}

FireSafety.description = 'Asks about fire safety features within a building';

export default FireSafety;
